import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAGES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { localInputToMelbourneISO, isoToLocalInput } from "@/lib/date";
import { Loader2, ChevronLeft } from "lucide-react";
import { Card } from "@/components/ui/card";

export default function PBOAdd() {
  const navigate = useNavigate();
  const { user, isViewAs } = useAuth();
  const [leaders, setLeaders] = useState([]);
  const [busy, setBusy] = useState(false);
  const now = new Date();
  const [form, setForm] = useState({
    name: "",
    partner_name: "",
    mg1_completed_at: isoToLocalInput(now.toISOString()),
    mg1_coaches: [],
    next_stage: "MG2/BP1",
    next_meeting_at: "",
  });

  useEffect(() => {
    // Coaches for MG1 = leaders in same team (or admin), unlimited scope for admins
    const params = user?.upline_team ? { team: user.upline_team } : {};
    api.get("/leaders", { params }).then((r) => setLeaders(r.data || []));
  }, [user?.upline_team]);

  if (isViewAs) return <Navigate to="/pbos" replace />;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCoach = (name) => {
    setForm((f) => ({
      ...f,
      mg1_coaches: f.mg1_coaches.includes(name) ? f.mg1_coaches.filter((x) => x !== name) : [...f.mg1_coaches, name],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = {
        name: form.name,
        partner_name: form.partner_name || null,
        mg1_completed_at: form.mg1_completed_at ? localInputToMelbourneISO(form.mg1_completed_at) : null,
        mg1_coaches: form.mg1_coaches,
        next_meeting: form.next_meeting_at ? {
          stage: form.next_stage,
          at: localInputToMelbourneISO(form.next_meeting_at),
        } : null,
      };
      const { data } = await api.post("/pbos", body);
      toast.success("PBO added");
      navigate(`/pbos/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="pbo-add-page">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-slate-800">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">New</div>
        <h1 className="font-serif text-3xl text-[#0A1F44] mt-1">Add PBO</h1>
        <p className="text-sm text-slate-500 mt-1">
          You are the IBO/Partner. MG1 is recorded as complete on save — this PBO is entering your pipeline.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <Card className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="PBO name" required>
              <Input data-testid="add-name" value={form.name} onChange={(e) => upd("name", e.target.value)} required />
            </Field>
            <Field label="Partner / spouse (optional)">
              <Input data-testid="add-partner" value={form.partner_name} onChange={(e) => upd("partner_name", e.target.value)} />
            </Field>
          </div>
          <p className="text-xs text-slate-500" data-testid="add-privacy-hint">
            To protect PBO privacy, record only their name and appointment progress. Don't enter email, phone, address or other personal details.
          </p>
        </Card>

        <Card className="p-5 space-y-4">
          <div>
            <h3 className="font-serif text-base text-[#0A1F44]">MG1 completed</h3>
            <p className="text-xs text-slate-500 mt-0.5">Record when MG1 actually happened and which coach/es were on it.</p>
          </div>
          <Field label="MG1 date/time (Melbourne)">
            <Input type="datetime-local" data-testid="add-mg1-at" value={form.mg1_completed_at} onChange={(e) => upd("mg1_completed_at", e.target.value)} />
          </Field>
          <div>
            <Label className="text-sm text-slate-700">MG1 coach/es</Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="add-coaches">
              {leaders.length === 0 && <div className="text-xs text-slate-500">No leaders available.</div>}
              {leaders.map((c) => {
                const checked = form.mg1_coaches.includes(c.name);
                return (
                  <label key={c.id} className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer text-sm ${checked ? "bg-[#0A1F44]/5 border-[#0A1F44]/30" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                    <Checkbox checked={checked} onCheckedChange={() => toggleCoach(c.name)} data-testid={`coach-${c.name}`} />
                    <span className="text-slate-800">{c.name}</span>
                    <span className="text-[10px] text-slate-400 ml-auto uppercase">{c.role}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div>
            <h3 className="font-serif text-base text-[#0A1F44]">Next appointment (optional)</h3>
            <p className="text-xs text-slate-500 mt-0.5">Schedule when their next stage will run. This is booked, not completed.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Stage">
              <Select value={form.next_stage} onValueChange={(v) => upd("next_stage", v)}>
                <SelectTrigger data-testid="add-next-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.slice(1).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date & time (Melbourne)">
              <Input type="datetime-local" data-testid="add-meeting" value={form.next_meeting_at} onChange={(e) => upd("next_meeting_at", e.target.value)} />
            </Field>
          </div>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
          <Button data-testid="add-submit" type="submit" disabled={busy} className="rounded-full bg-[#0A1F44] hover:bg-[#0A1F44]/90 h-11 px-6 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save PBO"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-700">{label} {required && <span className="text-[#8A1F1F]">*</span>}</Label>
      {children}
    </div>
  );
}
